import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SERVICES } from "@/data/site";

export default function Services() {
  const [active, setActive] = useState(null);

  return (
    <section id="services" data-testid="services-section" className="relative bg-[#F8FAFC] py-24 md:py-32">
      <div className="pz-container">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <span className="pz-overline">What we do</span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F172A]">
              Services built for brands<br />that demand precision.
            </h2>
            <div className="pz-gold-line mt-6" />
          </div>
          <p className="text-slate-600 max-w-md">
            Three core practices, one accountable team — designed to make every touchpoint of your brand look and feel premium.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.button
                key={s.id}
                type="button"
                onClick={() => setActive(s)}
                data-testid={`service-card-${s.id}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="group pz-card text-left p-0 overflow-hidden flex flex-col"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                  <div className="absolute top-5 left-5 w-12 h-12 rounded-xl bg-white text-[#0F172A] flex items-center justify-center shadow-lg">
                    <Icon className="w-6 h-6" strokeWidth={1.5} />
                  </div>
                  <div className="absolute top-5 right-5 w-10 h-10 rounded-full bg-[#F59E0B] text-[#0F172A] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                    <ArrowUpRight className="w-5 h-5" strokeWidth={2} />
                  </div>
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <h3 className="font-display text-2xl font-semibold text-[#0F172A] leading-tight">{s.title}</h3>
                  <p className="mt-3 text-slate-600 text-sm leading-relaxed">{s.short}</p>
                  <div className="mt-5 pt-5 border-t border-slate-100 flex flex-wrap gap-2">
                    {s.items.slice(0, 4).map((it) => (
                      <span key={it.label} className="text-[11px] uppercase tracking-wider text-slate-500">
                        {it.label} ·
                      </span>
                    ))}
                  </div>
                  <span className="mt-6 text-sm font-medium text-[#2563EB] inline-flex items-center gap-1.5 self-start">
                    Learn more <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Modal */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          data-testid="service-modal"
          className="max-w-3xl p-0 overflow-hidden border-0 rounded-3xl bg-white"
        >
          {active && (
            <div>
              <div className="relative h-56">
                <img src={active.image} alt={active.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] to-transparent" />
                <button
                  onClick={() => setActive(null)}
                  data-testid="service-modal-close"
                  className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-[#0F172A] flex items-center justify-center"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
                <div className="absolute bottom-5 left-7 right-7">
                  <DialogHeader className="text-left">
                    <DialogTitle className="font-display text-3xl font-bold text-white">{active.title}</DialogTitle>
                    <DialogDescription className="text-white/80 mt-1">{active.short}</DialogDescription>
                  </DialogHeader>
                </div>
              </div>
              <div className="p-7">
                <p className="text-slate-600 leading-relaxed">{active.blurb}</p>
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {active.items.map((it) => {
                    const I = it.icon;
                    return (
                      <div key={it.label} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#F8FAFC] border border-slate-100">
                        <I className="w-4 h-4 text-[#2563EB]" strokeWidth={1.6} />
                        <span className="text-sm text-slate-700">{it.label}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-7 flex flex-wrap gap-3">
                  <a
                    href="#contact"
                    onClick={() => setActive(null)}
                    data-testid="service-modal-cta"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full pz-btn-gold text-sm"
                  >
                    Request a Quote <ArrowUpRight className="w-4 h-4" />
                  </a>
                  <button
                    onClick={() => setActive(null)}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-slate-200 text-slate-700 text-sm hover:bg-slate-50"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
